import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { Pill } from "../atoms/Pill";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

export type ReportCertifyKind = "event" | "loan" | "batch";

export interface ReportFilterPanelProps {
	format: "pdf" | "csv";
	onFormatChange: (f: "pdf" | "csv") => void;
	kind: ReportCertifyKind;
	onKindChange: (k: ReportCertifyKind) => void;
	target: string;
	onTargetChange: (v: string) => void;
	dateFrom: string | null;
	onDateFromChange: (v: string | null) => void;
	dateTo: string | null;
	onDateToChange: (v: string | null) => void;
	onSubmit: () => void;
	submitting?: boolean;
	error?: string | null;
	className?: string;
}

const FORMAT_OPTIONS: Array<{ value: "pdf" | "csv"; label: string }> = [
	{ value: "pdf", label: "Certificate (PDF)" },
	{ value: "csv", label: "Data export (CSV)" },
];

// Auditors choose what they want to certify in plain terms. Batches are an
// internal grouping that mixes events from unrelated loans, so they are never
// offered here — certificates are always scoped to a single loan or event.
const CERTIFY_OPTIONS: Array<{ value: "loan" | "event"; label: string }> = [
	{ value: "loan", label: "A loan" },
	{ value: "event", label: "An event" },
];

interface TargetCopy {
	label: string;
	placeholder: string;
	example: string;
	helper: ReactNode;
}

const TARGET_COPY: Record<ReportCertifyKind, TargetCopy> = {
	loan: {
		label: "Loan ID",
		placeholder: "e.g. LN-2024-00187",
		example: "Example: LN-2024-00187",
		helper:
			"You'll find the loan ID at the top of every loan's page. Search for the loan, then copy its ID here.",
	},
	event: {
		label: "Event reference",
		placeholder: "e.g. evt_8f3a2b",
		example: "Example: evt_8f3a2b",
		helper:
			"Open the event you want to certify and copy its reference — or download the certificate straight from that event's page.",
	},
	// Legacy fallback only; not reachable from the type selector.
	batch: {
		label: "Reference",
		placeholder: "Enter a reference",
		example: "",
		helper: "Enter the reference for the record you want to certify.",
	},
};

/**
 * Guided certificate builder for the Reports page. The auditor picks a document
 * type and what they want to certify; the target field adapts with plain,
 * type-specific labels and examples. No raw hashes are ever requested.
 */
function ReportFilterPanel({
	format,
	onFormatChange,
	kind,
	onKindChange,
	target,
	onTargetChange,
	dateFrom,
	onDateFromChange,
	dateTo,
	onDateToChange,
	onSubmit,
	submitting = false,
	error = null,
	className,
}: ReportFilterPanelProps): ReactNode {
	const targetCopy = TARGET_COPY[kind];
	const submitLabel =
		format === "pdf" ? "Generate certificate" : "Generate data export";

	return (
		<div
			className={cn(
				"flex flex-col gap-4 rounded-lg border border-border bg-card p-5",
				className,
			)}
		>
			{/* Document type */}
			<div className="flex flex-col gap-1.5">
				<span className="text-xs font-medium text-mutedForeground">
					What kind of document?
				</span>
				<div className="flex gap-2" role="group" aria-label="Document type">
					{FORMAT_OPTIONS.map((opt) => (
						<button
							key={opt.value}
							type="button"
							onClick={() => onFormatChange(opt.value)}
							className="focus:outline-none focus:ring-2 focus:ring-ring rounded-full"
							aria-pressed={format === opt.value}
						>
							<Pill variant={format === opt.value ? "default" : "outline"}>
								{opt.label}
							</Pill>
						</button>
					))}
				</div>
			</div>

			{/* What to certify */}
			<div className="flex flex-col gap-1.5">
				<span className="text-xs font-medium text-mutedForeground">
					What do you want to certify?
				</span>
				<div
					className="flex gap-2"
					role="group"
					aria-label="What do you want to certify?"
				>
					{CERTIFY_OPTIONS.map((opt) => (
						<button
							key={opt.value}
							type="button"
							onClick={() => onKindChange(opt.value)}
							className="focus:outline-none focus:ring-2 focus:ring-ring rounded-full"
							aria-pressed={kind === opt.value}
						>
							<Pill variant={kind === opt.value ? "default" : "outline"}>
								{opt.label}
							</Pill>
						</button>
					))}
				</div>
			</div>

			{/* Type-specific target */}
			<div className="flex flex-col gap-1.5">
				<label
					htmlFor="report-target"
					className="text-xs font-medium text-mutedForeground"
				>
					{targetCopy.label}
				</label>
				<Input
					id="report-target"
					type="text"
					placeholder={targetCopy.placeholder}
					value={target}
					onChange={(e) => onTargetChange(e.target.value)}
				/>
				<Text size="xs" textColor="mutedForeground">
					{targetCopy.helper}
				</Text>
				{targetCopy.example && (
					<Text size="xs" textColor="mutedForeground">
						{targetCopy.example}
					</Text>
				)}
			</div>

			{/* Date range */}
			<div className="flex gap-3">
				<div className="flex flex-1 flex-col gap-1.5">
					<label
						htmlFor="report-date-from"
						className="text-xs font-medium text-mutedForeground"
					>
						From
					</label>
					<input
						id="report-date-from"
						type="date"
						value={dateFrom ?? ""}
						max={new Date().toISOString().split("T")[0]}
						onChange={(e) => onDateFromChange(e.target.value || null)}
						className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					/>
				</div>
				<div className="flex flex-1 flex-col gap-1.5">
					<label
						htmlFor="report-date-to"
						className="text-xs font-medium text-mutedForeground"
					>
						To
					</label>
					<input
						id="report-date-to"
						type="date"
						value={dateTo ?? ""}
						min={dateFrom ?? undefined}
						max={new Date().toISOString().split("T")[0]}
						onChange={(e) => onDateToChange(e.target.value || null)}
						className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
					/>
				</div>
			</div>

			{error && (
				<p role="alert" className="text-sm font-medium text-destructive">
					{error}
				</p>
			)}

			<Button
				type="button"
				onClick={onSubmit}
				disabled={submitting || target.trim().length === 0}
			>
				{submitting ? "Generating…" : submitLabel}
			</Button>
		</div>
	);
}
ReportFilterPanel.displayName = "ReportFilterPanel";

export default ReportFilterPanel;
export { ReportFilterPanel };
